import { Route } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { routes as propertiesRoutes } from './properties/routes';
import {UserService} from './core/services/user.service';
import {ImpersonationService} from './core/services/impersonation.service';
import {inject} from '@angular/core';
import {navigateToVersion1, shouldRedirectToVersion1} from './core/auth/version-redirect';

export const APP_ROUTES: Route[] = [
  {
    path: '',
    canActivate: [ MsalGuard, async () => {
      const userService = inject(UserService);
      const impersonation = inject(ImpersonationService);
      // Detect impersonation BEFORE loading the user so the interceptor tags the
      // /employees/me request and the backend resolves the impersonated identity.
      await impersonation.init();
      await userService.initUser();
      const user = userService.user();
      if (user && shouldRedirectToVersion1(user.tenantOptions, user.defaultTenant)) {
        const redirected = navigateToVersion1();
        if (redirected) {
          return false;
        }
      }
      return true;
    }],
    children: [
      {
        path: '',
        redirectTo: '/home',
        pathMatch: 'full',
      }, // Default route
      {
        path: 'home',
        loadChildren: () => import('./home/home.module').then((m) => m.HomeModule),
        data: { showSidebar: true, showHeader: true },
        canActivate: []
      },
      {
        path: 'deals',
        loadChildren: () => import('./deals/deals.module').then((m) => m.DealsModule),
        data: { showSidebar: true, showHeader: true },
        canActivate: []
      },
      {
        path: 'contacts',
        loadChildren: () => import('./contacts/contacts.module')
          .then((m) => m.ContactsModule),
        data: { showSidebar: true, showHeader: true },
        canActivate: []
      },
      {
        path: 'companies',
        loadChildren: () => import('./companies/companies.module')
          .then((m) => m.CompaniesModule),
        data: { showSidebar: true, showHeader: true },
        canActivate: []
      },
      {
        path: 'properties',
        children: propertiesRoutes,
        data: { showSidebar: true, showHeader: true },
        canActivate: []
      },
      {
        path: 'intel',
        loadComponent: () => import('./intel/components/intel/intel.component').then(m => m.IntelComponent),
        data: { showSidebar: true, showHeader: true },
        canActivate: []
      },
      {
        path: 'quotes-intelliegence-dashboard',
        loadComponent: () => import('./home/components/quotes-intelligence/quotes-intelligence.component').then(m => m.QuotesIntelligenceDashboard),
        data: { showSidebar: true, showHeader: true },
      },
      {
        path: 'search',
        loadChildren: () => import('./global-search/global-search.module')
          .then((m) => m.GlobalSearchModule),
        data: { showSidebar: true, showHeader: true },
        canActivate: []
      },
      {
        path: 'quote',
        loadChildren: () => import('./quotes/quotes.module')
          .then((m) => m.QuoteModule),
        data: { showSidebar: true, showHeader: true },
        canActivate: []
      },
      {
        path: 'add-entity',
        loadChildren: () => import('./add-entity/add-entity.module')
          .then((m) => m.AddEntityModule),
        data: { showSidebar: true, showHeader: true },
        canActivate: []
      },
      {
        path: 'query-tool',
        loadChildren: () => import('./query-tool/query-tool.module')
          .then((m) => m.QueryToolModule),
        canActivate: []
      },
      {
        path: 'merge-tool',
        loadChildren: () => import('./merge-tool/merge-tool.module')
          .then((m) => m.MergeToolModule),
        canActivate: []
      },
      {
        path: 'preferences',
        loadComponent: () => import('./manage-preferences/manage-preferences.component').then((c) => c.ManagePreferencesComponent),
        data: { showSidebar: true, showHeader: true },
        canActivate: []
      },
      {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module')
          .then((m) => m.SettingsModule),
        canActivate: []
      },
      {
        path: 'interactions-search',
        loadChildren: () => import('./interactions-search/interactions-search.module')
          .then((m) => m.InteractionsSearchModule),
        canActivate: []
      },
      {
        path: 'not-authorized',
        loadComponent: () => import('./shared/components/http-errors/not-authorized/not-authorized.component').then(m => m.NotAuthorizedComponent),
        data: { showSidebar: true, showHeader: true },
      },
      {
        path: 'not-found',
        loadComponent: () => import('./shared/components/http-errors/page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent),
        canActivate: [],
        data: { showSidebar: true, showHeader: true },
      },

    ]
  },
  {
    path: 'redirect',
    loadComponent: () => import('./core/auth/redirect.component').then(m => m.RedirectComponent),
  },
  {
    path: 'contacts',
    loadChildren: () => import('./contacts/contacts.module')
      .then((m) => m.ContactsModule),
    data: { showSidebar: true, showHeader: true },
    canActivate: []
  },
  {
    path: 'companies',
    loadChildren: () => import('./companies/companies.module')
      .then((m) => m.CompaniesModule),
    data: { showSidebar: true, showHeader: true },
    canActivate: []
  },
  {
    path: 'properties',
    children: propertiesRoutes,
    data: { showSidebar: true, showHeader: true },
    canActivate: []
  },
  {
    path: 'intel',
    loadComponent: () => import('./intel/components/intel/intel.component').then(m => m.IntelComponent),
    data: { showSidebar: true, showHeader: true },
    canActivate: []
  },
  {
    path: 'error',
    loadComponent: () => import('./error.component').then(m => m.ErrorComponent),
  },
  {
    path: '403',
    loadComponent: () => import('./shared/components/http-errors/not-authorized/not-authorized.component').then(m => m.NotAuthorizedComponent),
    data: { showSidebar: false, showHeader: false },
  },
  {
    path: '**',
    loadComponent: () => import('./shared/components/http-errors/page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent),
    data: { showSidebar: true, showHeader: true },
  }, // Wildcard route for 404 page
];
